import React from 'react';
import { X, Package, Tag, Calendar, Info } from 'lucide-react';
import { Producto } from '../../apis/productosDinamicosApi';

interface VerProductoProps {
  producto: Producto;
  onClose: () => void;
}

const VerProducto: React.FC<VerProductoProps> = ({ producto, onClose }) => {
  const formatDateTime = (dateTime?: string) => {
    if (!dateTime) return 'N/A';
    try {
      return new Date(dateTime).toLocaleString('es-MX');
    } catch {
      return dateTime;
    }
  };

  const styles = `
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.8);
      backdrop-filter: blur(8px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 1rem;
    }

    .modal-container {
      background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 20px;
      width: 100%;
      max-width: 800px;
      max-height: 90vh;
      overflow-y: auto;
      color: white;
      font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    }

    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 2rem 2rem 1rem;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .modal-title {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      font-size: 1.5rem;
      font-weight: 700;
      background: linear-gradient(135deg, #ffffff 0%, #007AFF 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .product-id {
      background: rgba(0, 122, 255, 0.1);
      color: #007AFF;
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.8rem;
      font-weight: 500;
      margin-left: 0.5rem;
    }

    .close-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: rgba(255, 255, 255, 0.1);
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 10px;
      color: white;
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .close-button:hover {
      background: rgba(255, 255, 255, 0.15);
      transform: scale(1.05);
    }

    .modal-content {
      padding: 2rem;
    }

    .info-section {
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 16px;
      padding: 1.5rem;
      margin-bottom: 1.5rem;
    }

    .section-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1.1rem;
      font-weight: 600;
      color: rgba(255, 255, 255, 0.9);
      margin-bottom: 1rem;
    }

    .info-item {
      display: flex;
      justify-content: space-between;
      padding: 0.75rem 0;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    }

    .info-item:last-child {
      border-bottom: none;
    }

    .info-label {
      font-weight: 500;
      color: rgba(255, 255, 255, 0.7);
    }

    .info-value {
      font-weight: 600;
      color: rgba(255, 255, 255, 0.95);
      text-align: right;
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      border-radius: 20px;
      font-size: 12px;
      font-weight: 500;
    }

    .status-active {
      background: rgba(16, 185, 129, 0.1);
      color: #10B981;
      border: 1px solid rgba(16, 185, 129, 0.3);
    }

    .status-inactive {
      background: rgba(239, 68, 68, 0.1);
      color: #EF4444;
      border: 1px solid rgba(239, 68, 68, 0.3);
    }

    .tag-info {
      background: rgba(0, 122, 255, 0.1);
      border: 1px solid rgba(0, 122, 255, 0.2);
      border-radius: 12px;
      padding: 1rem;
      text-align: center;
    }

    .tag-name {
      font-weight: 600;
      color: #007AFF;
      font-size: 1.1rem;
      margin-bottom: 0.5rem;
    }

    .tag-plate {
      font-family: 'Courier New', monospace;
      background: rgba(0, 122, 255, 0.2);
      color: #007AFF;
      padding: 0.5rem 1rem;
      border-radius: 8px;
      font-size: 1rem;
      display: inline-block;
    }

    .no-tag {
      color: rgba(255, 255, 255, 0.5);
      font-style: italic;
      text-align: center;
      padding: 2rem;
    }

    .modal-footer {
      display: flex;
      justify-content: center;
      padding: 1.5rem 2rem 2rem;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
    }

    .btn-close {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      font-weight: 500;
      font-size: 14px;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      border: 1px solid rgba(255, 255, 255, 0.2);
      cursor: pointer;
      transition: all 0.3s ease;
    }

    .btn-close:hover {
      background: rgba(255, 255, 255, 0.15);
      border-color: rgba(255, 255, 255, 0.3);
    }

    @media (max-width: 768px) {
      .modal-container {
        margin: 1rem;
        max-height: calc(100vh - 2rem);
      }

      .modal-header, .modal-content, .modal-footer {
        padding-left: 1.5rem;
        padding-right: 1.5rem;
      }

      .info-item {
        flex-direction: column;
        gap: 0.25rem;
      }

      .info-value {
        text-align: left;
      }
    }
  `;

  return (
    <>
      <style>{styles}</style>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h2 className="modal-title">
              <Package size={24} />
              Detalles del Producto
              <span className="product-id">#{producto.id}</span>
            </h2>
            <button onClick={onClose} className="close-button">
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="modal-content">
            {/* Información General */}
            <div className="info-section">
              <h3 className="section-title">
                <Info size={20} />
                Información General
              </h3>
              <div className="info-item">
                <span className="info-label">Tipo de Producto:</span>
                <span className="info-value">{producto.tipo_producto}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Estado:</span>
                <span className={`status-badge ${producto.activo ? 'status-active' : 'status-inactive'}`}>
                  <div style={{ 
                    width: 6, 
                    height: 6, 
                    borderRadius: '50%', 
                    backgroundColor: producto.activo ? '#10B981' : '#EF4444' 
                  }} />
                  {producto.activo ? 'Activo' : 'Inactivo'}
                </span>
              </div>
              <div className="info-item">
                <span className="info-label">Fecha de Creación:</span>
                <span className="info-value">{formatDateTime(producto.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Última Actualización:</span>
                <span className="info-value">{formatDateTime(producto.updated_at)}</span>
              </div>
            </div>

            {/* Datos del Producto */}
            <div className="info-section">
              <h3 className="section-title">
                <Package size={20} />
                Datos del Producto
              </h3>
              {producto.datos_mapeados && Object.keys(producto.datos_mapeados).length > 0 ? (
                Object.entries(producto.datos_mapeados).map(([key, value]) => (
                  <div key={key} className="info-item">
                    <span className="info-label">
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}:
                    </span>
                    <span className="info-value">{value || 'N/A'}</span>
                  </div>
                ))
              ) : (
                <div className="no-tag">
                  No hay datos específicos disponibles
                </div>
              )}
            </div>

            {/* Tag */}
            <div className="info-section">
              <h3 className="section-title">
                <Tag size={20} />
                Tag Asignada
              </h3>
              {(producto.tag_blac || producto.tag_tracksolid) ? (
                <div className="tag-info">
                  <div className="tag-name">{producto.tag_blac?.name_device || producto.tag_tracksolid?.device_name}</div>
                  <div className="tag-plate">{producto.tag_blac?.plate || producto.tag_tracksolid?.plate}</div>
                </div>
              ) : (
                <div className="no-tag">
                  No hay Tag asignada a este producto
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button onClick={onClose} className="btn-close">
              <X size={16} />
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default VerProducto;
